/**
 * Identity context, post-login return path sanitization (pure).
 *
 * A login begun on a consumer-protected screen (`/bookings`, `/inbox`, …) or the
 * "Log in to book" prompt returns the Consumer to that screen after a successful
 * login (docs/ux/screens/LogInScreen.md). The target rides in a `next` query
 * param / hidden form field, so it is attacker-controllable: we must never hand
 * an off-site URL to `redirect()`.
 *
 * Only same-origin, absolute in-app paths are allowed. Anything else (an absolute
 * URL, a protocol-relative `//host`, a backslash trick `/\host`, a bare relative
 * path, or a value carrying control characters) is refused so the caller falls
 * back to the role home.
 */
export function safeNextPath(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  // Must be an absolute in-app path: a single leading slash, no scheme, no host.
  if (!raw.startsWith("/")) return null;
  // Browsers fold backslashes to slashes, so "//host" and "/\host" both escape
  // the origin, reject both.
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
  // Reject any control character (< U+0020): it could smuggle a second
  // header/redirect target.
  for (let i = 0; i < raw.length; i++) {
    if (raw.charCodeAt(i) < 0x20) return null;
  }
  return raw;
}
